import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { useEvent } from '../../contexts/EventContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Link2, Plus, Edit, Trash2, ImageDown, Tags } from 'lucide-react';
import { toast } from 'sonner';
import WebhookLogsSection from './WebhookLogsSection';

export default function SettingsTab({ 
  currentUser,
  wooConfigs,
  events,
  fetchWooConfigs
}) {
  const { currentEvent, setCurrentEvent } = useEvent();
  
  // Settings state
  const [autoApprove, setAutoApprove] = useState(currentEvent?.auto_approve_cars || false);
  const [autoApproveManual, setAutoApproveManual] = useState(currentEvent?.auto_approve_manual_cars || false);
  const [leaderboardPublic, setLeaderboardPublic] = useState(currentEvent?.leaderboard_public || false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // WooCommerce config state
  const [showWooConfigModal, setShowWooConfigModal] = useState(false);
  const [selectedWooConfig, setSelectedWooConfig] = useState(null);
  const [wooConfigForm, setWooConfigForm] = useState({
    product_name: '',
    product_id: '',
    event_id: '',
    token_prefix: '',
    max_uses: 1,
    expires_days: 365
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [resizingPhotos, setResizingPhotos] = useState(false);
  const [resizeResult, setResizeResult] = useState(null);
  const [normalizingClasses, setNormalizingClasses] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [capInputGb, setCapInputGb] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [clearingGhosts, setClearingGhosts] = useState(false);

  // Fetch storage stats when this tab opens (super-admin only — the
  // backend gates the endpoint so we just no-op for regular admins).
  useEffect(() => {
    if (currentUser?.role !== 'super_admin') return;
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/storage/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) {
          setStorageStats(res.data);
          // Pre-fill cap input in GB (1 decimal) for editing.
          const gb = (res.data.cap_bytes || 0) / (1024 * 1024 * 1024);
          setCapInputGb(gb ? gb.toFixed(2).replace(/\.?0+$/, '') : '');
        }
      } catch (_e) {
        // silent — not critical
      }
    };
    load();
    return () => { cancelled = true; };
    // Re-fetch after a resize so the bar reflects the new total.
  }, [currentUser, resizeResult]);

  useEffect(() => {
    if (currentEvent) {
      setAutoApprove(currentEvent.auto_approve_cars || false);
      setAutoApproveManual(currentEvent.auto_approve_manual_cars || false);
      setLeaderboardPublic(currentEvent.leaderboard_public || false);
    }
  }, [currentEvent]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      if (!currentEvent?.id) {
        toast.error('No event selected');
        return;
      }
      await axios.put(`${API}/events/${currentEvent.id}`, {
        auto_approve_cars: autoApprove,
        auto_approve_manual_cars: autoApproveManual,
        leaderboard_public: leaderboardPublic
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentEvent({ ...currentEvent, auto_approve_cars: autoApprove, auto_approve_manual_cars: autoApproveManual, leaderboard_public: leaderboardPublic });
      
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast.error(errorMessage);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveCap = async () => {
    const gb = parseFloat(capInputGb);
    if (!gb || gb <= 0) {
      toast.error('Enter a valid storage cap in GB');
      return;
    }
    setSavingCap(true);
    try {
      const token = localStorage.getItem('token');
      const capBytes = Math.round(gb * 1024 * 1024 * 1024);
      await axios.put(`${API}/admin/storage/cap`, { cap_bytes: capBytes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Storage cap set to ${gb} GB`);
      // Refresh stats so the new cap and percentage render.
      const res = await axios.get(`${API}/admin/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageStats(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storage cap');
    } finally {
      setSavingCap(false);
    }
  };

  const handleClearGhostPhotos = async () => {
    if (!window.confirm('Clear placeholder/empty photo entries on cars? This nulls out photo_data fields shorter than 100 chars and cannot be undone.')) {
      return;
    }
    setClearingGhosts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/cars/clear-ghost-photos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cleared ${res.data.cleared} ghost photo entries`);
      // Refresh stats.
      const statsRes = await axios.get(`${API}/admin/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear ghost photos');
    } finally {
      setClearingGhosts(false);
    }
  };

  // ============= WOOCOMMERCE CONFIG =============
  
  const openCreateWooConfigModal = () => {
    setSelectedWooConfig(null);
    setWooConfigForm({
      product_name: '',
      product_id: '',
      event_id: currentEvent?.id || '',
      token_prefix: 'C4AC2026',
      max_uses: 1,
      expires_days: 365
    });
    setShowWooConfigModal(true);
  };

  const openEditWooConfigModal = (config) => {
    setSelectedWooConfig(config);
    setWooConfigForm({
      product_name: config.product_name,
      product_id: config.product_id || '',
      event_id: config.event_id,
      token_prefix: config.token_prefix || 'C4AC2026',
      max_uses: config.max_uses || 1,
      expires_days: config.expires_days || 365
    });
    setShowWooConfigModal(true);
  };

  const handleCreateWooConfig = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/woocommerce/config`, wooConfigForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product mapping created successfully');
      setShowWooConfigModal(false);
      fetchWooConfigs();
    } catch (error) {
      console.error('Error creating WooCommerce config:', error);
      toast.error(error.response?.data?.detail || 'Failed to create product mapping');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateWooConfig = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/woocommerce/config/${selectedWooConfig.id}`, wooConfigForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product mapping updated successfully');
      setShowWooConfigModal(false);
      setSelectedWooConfig(null);
      fetchWooConfigs();
    } catch (error) {
      console.error('Error updating WooCommerce config:', error);
      toast.error(error.response?.data?.detail || 'Failed to update product mapping');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWooConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this product mapping?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/woocommerce/config/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product mapping deleted successfully');
      fetchWooConfigs();
    } catch (error) {
      console.error('Error deleting WooCommerce config:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete product mapping');
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhook/woocommerce`;

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024;
      i += 1;
    }
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const handleResizePhotos = async () => {
    const ok = window.confirm(
      'Resize all existing car photos in the database?\n\n' +
      'Each photo will be downsized to 1024px on its longest edge (JPEG q=80). ' +
      'This is safe and idempotent — already-small photos are skipped.'
    );
    if (!ok) return;
    setResizingPhotos(true);
    setResizeResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/cars/resize-photos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const r = res.data || {};
      setResizeResult(r);
      if (r.resized > 0) {
        toast.success(`Resized ${r.resized} of ${r.scanned} photos. Saved ${formatBytes(r.bytes_saved)} (${r.percent_saved}%).`);
      } else {
        toast.message(`Scanned ${r.scanned} photos. Nothing to resize — already optimized.`);
      }
    } catch (err) {
      console.error('Resize failed:', err);
      toast.error(err.response?.data?.detail || 'Resize failed');
    } finally {
      setResizingPhotos(false);
    }
  };

  const handleNormalizeClassNames = async (dryRun = false) => {
    if (!dryRun) {
      const ok = window.confirm(
        'Rewrite all car class names to match canonical class entries?\n\n' +
        'Cars whose class_name only differs by spacing, dash style, or ' +
        'casing from a class in this event will be updated to the exact ' +
        'canonical name. Cars that don\'t match any canonical class are ' +
        'left untouched and reported back so you can fix them by hand.\n\n' +
        'Run a dry-run first if you want to preview changes.'
      );
      if (!ok) return;
    }
    setNormalizingClasses(true);
    setNormalizeResult(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (currentEvent?.id) params.set('event_id', currentEvent.id);
      if (dryRun) params.set('dry_run', 'true');
      const res = await axios.post(
        `${API}/admin/cars/normalize-class-names?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = res.data || {};
      setNormalizeResult(r);
      if (dryRun) {
        toast.message(
          `Dry run: ${r.would_update} car(s) would be updated, ` +
          `${r.already_canonical} already canonical, ${r.unmatched} unmatched.`
        );
      } else if (r.updated > 0) {
        toast.success(`Updated ${r.updated} car(s) to canonical class names.`);
      } else {
        toast.message(
          `Scanned ${r.scanned} car(s). Nothing to update — all class names already canonical or unmatched.`
        );
      }
    } catch (err) {
      console.error('Normalize failed:', err);
      toast.error(err.response?.data?.detail || 'Normalize failed');
    } finally {
      setNormalizingClasses(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* General Settings */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Settings</h2>
        <p className="text-gray-600 text-sm mb-6">Settings for: {currentEvent?.name || 'No event selected'}</p>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <Label className="text-gray-900 text-lg font-medium">Auto-Approve WooCommerce Registrations</Label>
              <p className="text-gray-600 text-sm mt-1">
                When enabled, car registrations from WooCommerce orders are automatically approved without manual review.
              </p>
            </div>
            <Switch
              checked={autoApprove}
              onCheckedChange={setAutoApprove}
              className="ml-4"
              data-testid="auto-approve-switch"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <Label className="text-gray-900 text-lg font-medium">Auto-Approve Manual Registrations</Label>
              <p className="text-gray-600 text-sm mt-1">
                When enabled, registrations created from the Cars admin tab (Manual Registration form, walk-up reception) are automatically approved. Turn this on at show start so reception can register cars and print dash cards in one step.
              </p>
            </div>
            <Switch
              checked={autoApproveManual}
              onCheckedChange={setAutoApproveManual}
              className="ml-4"
              data-testid="auto-approve-manual-switch"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <Label className="text-gray-900 text-lg font-medium">Public Leaderboard</Label>
              <p className="text-gray-600 text-sm mt-1">
                When enabled, the leaderboard with judge scores will be visible to the public. Keep disabled until judging is complete.
              </p>
            </div>
            <Switch
              checked={leaderboardPublic}
              onCheckedChange={setLeaderboardPublic}
              className="ml-4"
              data-testid="leaderboard-public-switch"
            />
          </div>

          <Button 
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="bg-pink-600 hover:bg-pink-700"
            data-testid="save-settings-btn"
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* WooCommerce Integration - Super Admin Only */}
      {currentUser?.role === 'super_admin' && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">WooCommerce Integration</h2>
              <p className="text-gray-600 text-sm">Configure automatic car registration from WooCommerce orders</p>
            </div>
            <Button onClick={openCreateWooConfigModal} className="bg-pink-600 hover:bg-pink-700" data-testid="add-woo-config-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product Mapping
            </Button>
          </div>

          {/* Webhook URL */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-2">
              <Link2 className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-600 font-medium">Webhook URL</span>
            </div>
            <code className="text-sm text-blue-800 bg-blue-100 px-2 py-1 rounded block break-all">
              {webhookUrl}
            </code>
            <div className="text-blue-900 text-xs mt-3 space-y-1">
              <p className="font-semibold">Add TWO webhooks in WooCommerce → Settings → Advanced → Webhooks:</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1">
                <li><span className="font-semibold">Order created</span> — fires on new orders.</li>
                <li><span className="font-semibold">Order updated</span> — fires when an order changes status (e.g., pending → completed). Required so registrations auto-create when payment is confirmed after the initial order.</li>
              </ol>
              <p className="mt-1">Both webhooks point to the same URL above. Registrations are only created when the order status reaches <span className="font-mono font-semibold">completed</span>.</p>
            </div>
          </div>

          {/* Product Mappings */}
          {wooConfigs.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-600">No product mappings configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              {wooConfigs.map(config => (
                <div 
                  key={config.id} 
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <h4 className="text-gray-900 font-medium">{config.product_name}</h4>
                    <p className="text-gray-600 text-sm">
                      → {events.find(e => e.id === config.event_id)?.name || 'Unknown Event'}
                    </p>
                    {config.product_id && (
                      <p className="text-gray-500 text-xs">Product ID: {config.product_id}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      config.active !== false 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {config.active !== false ? 'Active' : 'Inactive'}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditWooConfigModal(config)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteWooConfig(config.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Webhook Logs - Super Admin Only */}
      {currentUser?.role === 'super_admin' && (
        <WebhookLogsSection />
      )}

      {/* Maintenance - Super Admin Only */}
      {currentUser?.role === 'super_admin' && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-testid="maintenance-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Maintenance</h2>
          <p className="text-gray-600 text-sm mb-6">One-off cleanups and optimizations.</p>

          {/* Storage status bar */}
          {storageStats && (
            <div className="mb-6 p-4 border border-gray-200 rounded-lg" data-testid="storage-status-bar">
              {(() => {
                const pct = storageStats.percent_used || 0;
                const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
                const textColor = pct >= 90 ? 'text-red-700' : pct >= 70 ? 'text-amber-700' : 'text-emerald-700';
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-900 text-lg font-medium">Database storage</Label>
                      <span className={`text-sm font-semibold ${textColor}`}>
                        {formatBytes(storageStats.db_data_bytes)} / {formatBytes(storageStats.cap_bytes)} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-3">
                      <div
                        className={`h-full ${barColor} transition-all duration-500`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                        data-testid="storage-bar-fill"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-700">
                      <div>
                        <div className="text-gray-500 uppercase tracking-wide">Total cars</div>
                        <div className="text-base font-semibold text-gray-900" data-testid="storage-total-cars">{storageStats.total_cars}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 uppercase tracking-wide">With photos</div>
                        <div className="text-base font-semibold text-gray-900" data-testid="storage-cars-with-photos">{storageStats.cars_with_photos}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 uppercase tracking-wide">Avg photo</div>
                        <div className="text-base font-semibold text-gray-900">{formatBytes(storageStats.photo_bytes_avg)}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 uppercase tracking-wide">All photos</div>
                        <div className="text-base font-semibold text-gray-900">{formatBytes(storageStats.photo_bytes_total)}</div>
                      </div>
                    </div>

                    {/* Ghost photos: rows that have photo_data but it's too short
                        to be a real image (e.g., empty data-URI prefix). These
                        inflate the "with photos" count without using real bytes. */}
                    {storageStats.ghost_photo_count > 0 && (
                      <div className="mt-4 p-3 border border-amber-300 bg-amber-50 rounded text-sm flex items-start justify-between gap-3 flex-wrap" data-testid="ghost-photos-banner">
                        <div className="text-amber-800 flex-1 min-w-[220px]">
                          <div className="font-semibold">{storageStats.ghost_photo_count} placeholder photo entries detected</div>
                          <div className="text-xs mt-1">
                            These cars have a `photo_data` value that's too short to be a real image
                            (likely an empty data-URI header). They were previously inflating the
                            "with photos" count. Clearing nulls them out so the count is accurate.
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleClearGhostPhotos}
                          disabled={clearingGhosts}
                          className="text-amber-800 border-amber-400 hover:bg-amber-100"
                          data-testid="clear-ghost-photos-btn"
                        >
                          {clearingGhosts ? 'Clearing…' : 'Clear placeholders'}
                        </Button>
                      </div>
                    )}

                    {/* Storage cap editor — super admin can match the cap to
                        their actual MongoDB Atlas tier without touching env vars. */}
                    <div className="mt-4 p-3 border border-gray-200 bg-gray-50 rounded">
                      <Label className="text-gray-700 text-sm font-medium">Storage cap (GB)</Label>
                      <div className="text-xs text-gray-500 mb-2">
                        Sets the soft limit that drives this progress bar. Match it to your MongoDB Atlas tier (e.g. M0=0.5, M2=2, M5=5, M10=10).
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Input
                          type="number"
                          step="0.5"
                          min="0.1"
                          value={capInputGb}
                          onChange={(e) => setCapInputGb(e.target.value)}
                          className="w-32 bg-white"
                          data-testid="storage-cap-input"
                        />
                        <span className="text-sm text-gray-600">GB</span>
                        <Button
                          size="sm"
                          onClick={handleSaveCap}
                          disabled={savingCap}
                          className="bg-pink-600 hover:bg-pink-700 text-white"
                          data-testid="save-storage-cap-btn"
                        >
                          {savingCap ? 'Saving…' : 'Save cap'}
                        </Button>
                      </div>
                    </div>
                    {pct >= 70 && (
                      <div className={`mt-3 text-sm ${textColor}`}>
                        {pct >= 90
                          ? 'Storage is nearly full. Resize existing photos and consider raising your database tier.'
                          : 'Storage usage is climbing. Running the Resize photos action below will reclaim space.'}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          <div className="flex items-start justify-between gap-4 p-4 border border-gray-200 rounded-lg flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <Label className="text-gray-900 text-lg font-medium flex items-center gap-2">
                <ImageDown className="w-5 h-5 text-pink-600" />
                Resize existing car photos
              </Label>
              <p className="text-gray-600 text-sm mt-1">
                Downsizes every car photo already stored in the database to 1024px on the longest edge (JPEG q=80).
                New uploads are resized automatically — this only matters for photos uploaded before that change.
                Safe to run anytime; already-small photos are skipped.
              </p>
              {resizeResult && (
                <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-3" data-testid="resize-result">
                  Scanned <span className="font-semibold">{resizeResult.scanned}</span>,
                  resized <span className="font-semibold">{resizeResult.resized}</span>,
                  skipped <span className="font-semibold">{resizeResult.skipped}</span>
                  {resizeResult.failed > 0 && <>, failed <span className="font-semibold text-red-600">{resizeResult.failed}</span></>}
                  . Saved <span className="font-semibold">{formatBytes(resizeResult.bytes_saved)}</span>
                  {' '}({resizeResult.percent_saved}%).
                </div>
              )}
            </div>
            <Button
              onClick={handleResizePhotos}
              disabled={resizingPhotos}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="resize-photos-btn"
            >
              <ImageDown className={`w-4 h-4 mr-2 ${resizingPhotos ? 'animate-pulse' : ''}`} />
              {resizingPhotos ? 'Resizing...' : 'Resize photos'}
            </Button>
          </div>

          <div className="flex items-start justify-between gap-4 p-4 border border-gray-200 rounded-lg flex-wrap mt-4">
            <div className="flex-1 min-w-[280px]">
              <Label className="text-gray-900 text-lg font-medium flex items-center gap-2">
                <Tags className="w-5 h-5 text-pink-600" />
                Normalize car class names
              </Label>
              <p className="text-gray-600 text-sm mt-1">
                Rewrites every car's <span className="font-mono">class_name</span> to exactly match the canonical
                class entries for this event. Fixes mismatches caused by stray spaces around dashes,
                en-dash vs hyphen, or zero-width characters — which would otherwise split a class
                into multiple groups in the printed leaderboard. Run a dry-run first to preview.
              </p>
              {normalizeResult && (
                <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-3 space-y-1" data-testid="normalize-result">
                  <div>
                    Scanned <span className="font-semibold">{normalizeResult.scanned}</span>,
                    {' '}canonical classes <span className="font-semibold">{normalizeResult.canonical_class_count}</span>,
                    {' '}already-canonical <span className="font-semibold">{normalizeResult.already_canonical}</span>,
                    {' '}{normalizeResult.dry_run ? 'would update' : 'updated'}{' '}
                    <span className="font-semibold">
                      {normalizeResult.dry_run ? normalizeResult.would_update : normalizeResult.updated}
                    </span>,
                    {' '}unmatched <span className="font-semibold text-amber-700">{normalizeResult.unmatched}</span>.
                  </div>
                  {normalizeResult.unmatched_samples?.length > 0 && (
                    <div>
                      <div className="font-semibold mt-2">Unmatched class names (top {normalizeResult.unmatched_samples.length}):</div>
                      <ul className="list-disc list-inside">
                        {normalizeResult.unmatched_samples.map((s, i) => (
                          <li key={i}>
                            <span className="font-mono">"{s.class_name}"</span> — {s.count} car(s)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => handleNormalizeClassNames(true)}
                disabled={normalizingClasses}
                variant="outline"
                className="border-gray-300 text-gray-900 hover:bg-gray-50"
                data-testid="normalize-classes-dryrun-btn"
              >
                <Tags className={`w-4 h-4 mr-2 ${normalizingClasses ? 'animate-pulse' : ''}`} />
                Dry run
              </Button>
              <Button
                onClick={() => handleNormalizeClassNames(false)}
                disabled={normalizingClasses}
                className="bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="normalize-classes-btn"
              >
                <Tags className={`w-4 h-4 mr-2 ${normalizingClasses ? 'animate-pulse' : ''}`} />
                {normalizingClasses ? 'Working...' : 'Normalize classes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WooCommerce Config Modal */}
      <Dialog open={showWooConfigModal} onOpenChange={setShowWooConfigModal}>
        <DialogContent className="max-w-lg bg-white text-gray-900 border-gray-300">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 font-bold">
              {selectedWooConfig ? 'Edit Product Mapping' : 'Add Product Mapping'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedWooConfig ? 'Update the WooCommerce product mapping' : 'Map a WooCommerce product to an event'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={selectedWooConfig ? handleUpdateWooConfig : handleCreateWooConfig} className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input
                value={wooConfigForm.product_name}
                onChange={(e) => setWooConfigForm({...wooConfigForm, product_name: e.target.value})}
                required
                placeholder="e.g., 2026 Car Show Entry"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="woo-product-name-input"
              />
              <p className="text-gray-500 text-xs mt-1">Must match the product name in WooCommerce exactly</p>
            </div>

            <div>
              <Label>Product ID (Optional)</Label>
              <Input
                value={wooConfigForm.product_id}
                onChange={(e) => setWooConfigForm({...wooConfigForm, product_id: e.target.value})}
                placeholder="e.g., 12345"
                className="bg-white border-gray-300 text-gray-900"
              />
              <p className="text-gray-500 text-xs mt-1">WooCommerce product ID for more reliable matching</p>
            </div>

            <div>
              <Label>Target Event *</Label>
              <Select
                value={wooConfigForm.event_id}
                onValueChange={(value) => setWooConfigForm({...wooConfigForm, event_id: value})}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowWooConfigModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="woo-config-submit-btn">
                {submitting ? 'Saving...' : selectedWooConfig ? 'Update Mapping' : 'Add Mapping'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
